/** Llamadas HTTP del feature Continuidad de Servicio (Sprint 3 SISS).
 *
 * Cubre los 3 sub-módulos:
 * - Continuidad de Agua Potable (interrupciones + producción diaria).
 * - Continuidad de Alcantarillado (obstrucciones).
 * - Presiones (PCPs + mediciones).
 */
import { apiDownload, apiRequest } from "../../lib/api";

// ────────────────────────────────────────────────────────────────────────
// Continuidad Agua — Interrupciones
// ────────────────────────────────────────────────────────────────────────

export type TipoInterrupcionAgua =
  | "Programada"
  | "FuerzaMayor"
  | "CasoFortuito";
export type NivelAfectacion = "Total" | "Parcial";

export interface InterrupcionAgua {
  id: number;
  empresa_id: number;
  /** "N° de Registro" del manual SISS: correlativo por comité. */
  numero_registro: number;
  tipo: TipoInterrupcionAgua;
  nivel_afectacion: NivelAfectacion;
  fecha_inicio: string;
  fecha_termino: string | null;
  motivo: string;
  ubicacion_falla: string | null;
  usuarios_afectados: number;
  diametro_matriz_mm: number | null;
  material_matriz: string | null;
  longitud_tramo_m: string | null;
  observaciones: string | null;
  fecha_aviso_usuarios: string | null;
  fecha_aviso_siss: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
  /** Respaldo (aviso a usuarios / SISS, fotos). */
  evidencias?: { key: string; nombre: string; tipo: string }[];
}

export interface InterrupcionAguaCreateInput {
  tipo: TipoInterrupcionAgua;
  nivel_afectacion: NivelAfectacion;
  fecha_inicio: string;
  fecha_termino?: string | null;
  motivo: string;
  ubicacion_falla?: string | null;
  usuarios_afectados?: number;
  diametro_matriz_mm?: number | null;
  material_matriz?: string | null;
  longitud_tramo_m?: string | null;
  observaciones?: string | null;
  fecha_aviso_usuarios?: string | null;
  fecha_aviso_siss?: string | null;
}

export interface InterrupcionListResponse {
  items: InterrupcionAgua[];
  total: number;
  limit: number;
  offset: number;
}

export interface InterrupcionFilters {
  tipo?: TipoInterrupcionAgua | null;
  nivel?: NivelAfectacion | null;
  desde?: string | null;
  hasta?: string | null;
  limit?: number;
  offset?: number;
}

function toQuery(filters: object): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === null || v === undefined || v === "") continue;
    params.set(k, String(v));
  }
  return params.toString();
}

export function fetchInterrupciones(
  filters: InterrupcionFilters = {},
): Promise<InterrupcionListResponse> {
  const qs = toQuery(filters);
  return apiRequest<InterrupcionListResponse>(
    `/api/v1/continuidad/agua/interrupciones${qs ? `?${qs}` : ""}`,
  );
}

export function crearInterrupcion(
  payload: InterrupcionAguaCreateInput,
): Promise<InterrupcionAgua> {
  return apiRequest<InterrupcionAgua>(
    "/api/v1/continuidad/agua/interrupciones",
    { method: "POST", body: payload },
  );
}

export function eliminarInterrupcion(id: number): Promise<void> {
  return apiRequest<void>(`/api/v1/continuidad/agua/interrupciones/${id}`, {
    method: "DELETE",
  });
}

export function descargarCsvInterrupciones(
  filters: InterrupcionFilters = {},
): Promise<void> {
  const qs = toQuery(filters);
  return apiDownload(
    `/api/v1/continuidad/agua/interrupciones/export.csv${qs ? `?${qs}` : ""}`,
    "interrupciones_agua.csv",
  );
}

// ────────────────────────────────────────────────────────────────────────
// Continuidad Agua — Producción diaria
// ────────────────────────────────────────────────────────────────────────

export interface FuenteProduccion {
  fuente: string;
  tipo: "pozo" | "aljibe" | "otro";
  m3: string;
  horas?: string | null;
}

export interface ProduccionDiaria {
  id: number;
  empresa_id: number;
  fecha: string;
  detalle_fuentes: FuenteProduccion[];
  total_m3: string;
  observaciones: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface ProduccionCreateInput {
  fecha: string;
  detalle_fuentes: FuenteProduccion[];
  observaciones?: string | null;
}

export function fetchProduccion(
  desde: string,
  hasta: string,
): Promise<{ items: ProduccionDiaria[] }> {
  return apiRequest<{ items: ProduccionDiaria[] }>(
    `/api/v1/continuidad/agua/produccion?desde=${desde}&hasta=${hasta}`,
  );
}

export function crearProduccion(
  payload: ProduccionCreateInput,
): Promise<ProduccionDiaria> {
  return apiRequest<ProduccionDiaria>("/api/v1/continuidad/agua/produccion", {
    method: "POST",
    body: payload,
  });
}

export function descargarCsvProduccion(
  desde: string,
  hasta: string,
): Promise<void> {
  return apiDownload(
    `/api/v1/continuidad/agua/produccion/export.csv?desde=${desde}&hasta=${hasta}`,
    `produccion_agua_${desde}_a_${hasta}.csv`,
  );
}

// ────────────────────────────────────────────────────────────────────────
// Continuidad Alcantarillado — Obstrucciones
// ────────────────────────────────────────────────────────────────────────

export type TipoObstruccion = "FuerzaMayor" | "CasoFortuito";
export type InfraestructuraAfectada =
  | "InstalacionDomiciliaria"
  | "UnionDomiciliaria"
  | "Colector"
  | "CamaraInspeccion"
  | "TramoRedEntreCamaras";

export interface Obstruccion {
  id: number;
  empresa_id: number;
  numero_registro: number;
  tipo: TipoObstruccion;
  fecha_inicio: string;
  fecha_termino: string | null;
  motivo: string;
  ubicacion_falla: string | null;
  usuarios_afectados: number;
  afloramiento: boolean;
  alcanzo_curso_agua: boolean;
  infraestructura_afectada: InfraestructuraAfectada;
  diametro_mm: number | null;
  material: string | null;
  observaciones: string | null;
  fecha_aviso_siss: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface ObstruccionCreateInput {
  tipo: TipoObstruccion;
  fecha_inicio: string;
  fecha_termino?: string | null;
  motivo: string;
  ubicacion_falla?: string | null;
  usuarios_afectados?: number;
  afloramiento?: boolean;
  alcanzo_curso_agua?: boolean;
  infraestructura_afectada: InfraestructuraAfectada;
  diametro_mm?: number | null;
  material?: string | null;
  observaciones?: string | null;
  fecha_aviso_siss?: string | null;
}

export interface ObstruccionListResponse {
  items: Obstruccion[];
  total: number;
  limit: number;
  offset: number;
}

export interface ObstruccionFilters {
  tipo?: TipoObstruccion | null;
  infraestructura?: InfraestructuraAfectada | null;
  desde?: string | null;
  hasta?: string | null;
  limit?: number;
  offset?: number;
}

export function fetchObstrucciones(
  filters: ObstruccionFilters = {},
): Promise<ObstruccionListResponse> {
  const qs = toQuery(filters);
  return apiRequest<ObstruccionListResponse>(
    `/api/v1/continuidad/alcantarillado/obstrucciones${qs ? `?${qs}` : ""}`,
  );
}

export function crearObstruccion(
  payload: ObstruccionCreateInput,
): Promise<Obstruccion> {
  return apiRequest<Obstruccion>(
    "/api/v1/continuidad/alcantarillado/obstrucciones",
    { method: "POST", body: payload },
  );
}

export function eliminarObstruccion(id: number): Promise<void> {
  return apiRequest<void>(
    `/api/v1/continuidad/alcantarillado/obstrucciones/${id}`,
    { method: "DELETE" },
  );
}

export function descargarCsvObstrucciones(
  filters: ObstruccionFilters = {},
): Promise<void> {
  const qs = toQuery(filters);
  return apiDownload(
    `/api/v1/continuidad/alcantarillado/obstrucciones/export.csv${qs ? `?${qs}` : ""}`,
    "obstrucciones_alcantarillado.csv",
  );
}

// ────────────────────────────────────────────────────────────────────────
// Presiones — PCPs + Mediciones
// ────────────────────────────────────────────────────────────────────────

export type TipoPCP = "Minima" | "Maxima";
export type UnidadPresion = "mca" | "bar" | "psi";
export type EquipamientoPresion =
  | "Manometro"
  | "ManometroAgujaArrastre"
  | "Datalogger";

export interface PCP {
  id: number;
  empresa_id: number;
  codigo: string;
  ubicacion: string;
  tipo: TipoPCP;
  activo: boolean;
  observaciones: string | null;
  created_at: string;
  updated_at: string;
}

export interface PCPCreateInput {
  codigo: string;
  ubicacion: string;
  tipo: TipoPCP;
  activo?: boolean;
  observaciones?: string | null;
}

export function fetchPCPs(onlyActive = false): Promise<PCP[]> {
  return apiRequest<PCP[]>(
    `/api/v1/continuidad/presiones/puntos${onlyActive ? "?only_active=true" : ""}`,
  );
}

export function crearPCP(payload: PCPCreateInput): Promise<PCP> {
  return apiRequest<PCP>("/api/v1/continuidad/presiones/puntos", {
    method: "POST",
    body: payload,
  });
}

export function desactivarPCP(id: number): Promise<PCP> {
  return apiRequest<PCP>(
    `/api/v1/continuidad/presiones/puntos/${id}/desactivar`,
    { method: "POST" },
  );
}

export interface MedicionPresion {
  id: number;
  empresa_id: number;
  punto_id: number;
  fecha_medicion: string;
  responsable: string;
  valor: string;
  unidad: UnidadPresion;
  equipamiento: EquipamientoPresion;
  fuera_de_rango: boolean;
  observaciones: string | null;
  created_by_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface MedicionCreateInput {
  punto_id: number;
  fecha_medicion: string;
  responsable: string;
  valor: string;
  unidad?: UnidadPresion;
  equipamiento: EquipamientoPresion;
  observaciones?: string | null;
}

export interface MedicionListResponse {
  items: MedicionPresion[];
  total: number;
  limit: number;
  offset: number;
}

export interface MedicionFilters {
  punto_id?: number | null;
  solo_fuera_de_rango?: boolean;
  desde?: string | null;
  hasta?: string | null;
  limit?: number;
  offset?: number;
}

export function fetchMediciones(
  filters: MedicionFilters = {},
): Promise<MedicionListResponse> {
  const qs = toQuery(filters);
  return apiRequest<MedicionListResponse>(
    `/api/v1/continuidad/presiones/mediciones${qs ? `?${qs}` : ""}`,
  );
}

export function crearMedicion(
  payload: MedicionCreateInput,
): Promise<MedicionPresion> {
  return apiRequest<MedicionPresion>(
    "/api/v1/continuidad/presiones/mediciones",
    { method: "POST", body: payload },
  );
}

export function descargarCsvMediciones(
  filters: MedicionFilters = {},
): Promise<void> {
  const qs = toQuery(filters);
  return apiDownload(
    `/api/v1/continuidad/presiones/mediciones/export.csv${qs ? `?${qs}` : ""}`,
    "mediciones_presion.csv",
  );
}

// ────────────────────────────────────────────────────────────────────────
// Etiquetas legibles
// ────────────────────────────────────────────────────────────────────────

export const TIPO_INTERRUPCION_LABELS: Record<TipoInterrupcionAgua, string> = {
  Programada: "Programada",
  FuerzaMayor: "Fuerza mayor",
  CasoFortuito: "Caso fortuito",
};

export const TIPO_OBSTRUCCION_LABELS: Record<TipoObstruccion, string> = {
  FuerzaMayor: "Fuerza mayor",
  CasoFortuito: "Caso fortuito",
};

export const INFRA_LABELS: Record<InfraestructuraAfectada, string> = {
  InstalacionDomiciliaria: "Instalación domiciliaria",
  UnionDomiciliaria: "Unión domiciliaria",
  Colector: "Colector",
  CamaraInspeccion: "Cámara de inspección",
  TramoRedEntreCamaras: "Tramo de red entre cámaras",
};

export const TIPO_PCP_LABELS: Record<TipoPCP, string> = {
  Minima: "Mínima (extremo más alejado)",
  Maxima: "Máxima (cercano a estanque)",
};

export const EQUIP_LABELS: Record<EquipamientoPresion, string> = {
  Manometro: "Manómetro",
  ManometroAgujaArrastre: "Manómetro con aguja de arrastre",
  Datalogger: "Datalogger",
};

export const UNIDAD_LABELS: Record<UnidadPresion, string> = {
  mca: "m.c.a.",
  bar: "BAR",
  psi: "PSI",
};
